import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  ChefHat,
  UtensilsCrossed,
  ListChecks,
  Calendar,
  Settings,
  LogOut,
  Sun,
  Moon,
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useTheme } from '@/lib/theme';
import { cn } from '@/lib/cn';

const NAV = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/admin/clientes', label: 'Clientes', icon: Users },
  { to: '/admin/recetas', label: 'Recetas', icon: ChefHat },
  { to: '/admin/platos', label: 'Platos', icon: UtensilsCrossed },
  { to: '/admin/menus', label: 'Menús', icon: ListChecks },
  { to: '/admin/planes', label: 'Planes', icon: Calendar },
  { to: '/admin/settings', label: 'Ajustes', icon: Settings },
];

export function AdminLayout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();

  async function handleLogout() {
    await logout();
    nav('/login', { replace: true });
  }

  return (
    <div className="min-h-full grid md:grid-cols-[260px_1fr] grid-cols-1">
      {/* SIDEBAR (desktop) */}
      <aside className="hidden md:flex flex-col bg-card border-r border-border">
        <div className="p-5 flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-accent flex items-center justify-center font-extrabold text-white">
            D
          </div>
          <div>
            <p className="font-extrabold text-base leading-none">diegoNutri</p>
            <p className="text-xs text-text-dim">Panel del coach</p>
          </div>
        </div>

        <nav className="flex-1 px-3 py-2 space-y-0.5">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-[var(--r-sm)] text-sm font-semibold transition-colors',
                  isActive
                    ? 'bg-accent-dim text-accent'
                    : 'text-text-dim hover:bg-bg hover:text-text',
                )
              }
            >
              <item.icon size={18} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-border space-y-2">
          <button
            type="button"
            onClick={toggle}
            aria-label="Cambiar tema"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-[var(--r-sm)] text-sm font-semibold text-text-dim hover:bg-bg hover:text-text"
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            {theme === 'dark' ? 'Modo claro' : 'Modo oscuro'}
          </button>
          <div className="px-3 py-2 text-sm">
            <p className="font-semibold truncate">{user?.email}</p>
            <p className="text-xs text-text-dim">Coach</p>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-[var(--r-sm)] text-sm font-semibold text-text-dim hover:bg-danger-dim hover:text-danger"
          >
            <LogOut size={18} />
            Cerrar Sesión
          </button>
        </div>
      </aside>

      {/* MAIN + bottom tabs (mobile) */}
      <div className="flex flex-col min-h-full pb-24 md:pb-0">
        {/* Top bar móvil con brand + theme */}
        <header className="md:hidden flex items-center justify-between px-4 h-14 border-b border-border bg-card">
          <Link to="/admin" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-accent flex items-center justify-center font-extrabold text-white text-sm">
              D
            </div>
            <span className="font-extrabold">diegoNutri</span>
          </Link>
          <button
            type="button"
            onClick={toggle}
            aria-label="Cambiar tema"
            className="p-2 rounded text-text-dim hover:bg-bg"
          >
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </header>

        <main className="flex-1 p-4 md:p-8 max-w-7xl w-full mx-auto">
          <Outlet />
        </main>

        {/* Bottom tabs (móvil) — 5 de 7 items para caber en 375px */}
        <nav className="md:hidden fixed bottom-0 inset-x-0 bg-card border-t border-border flex justify-around py-2 z-30">
          {NAV.slice(0, 5).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  'flex flex-col items-center gap-0.5 px-1.5 py-1 text-[10px] font-semibold flex-1',
                  isActive ? 'text-accent' : 'text-text-dim',
                )
              }
            >
              <item.icon size={18} />
              <span className="truncate max-w-full">{item.label}</span>
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
